import {CommonModule} from '@angular/common';
import {Component, EventEmitter, OnInit, Output, signal} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors
} from '@angular/forms';
import {MatDialogModule} from '@angular/material/dialog';
import {get} from 'lodash';
import {DpDatePickerModule} from 'ng2-date-picker';
import {TranslateModule} from '@ngx-translate/core';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {
  DatetimePickerComponent,
  DialogCloseButtonComponent,
  InfoMessageComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent,
  SelectModule,
  TextareaFieldComponent,
  TextFieldComponent
} from '@q9elements/ui-kit/components';
import {Release} from '@q9elements/ui-kit/common';
import {format, getUnixTime} from 'date-fns';
import {utcToZonedTime, zonedTimeToUtc} from 'date-fns-tz';
import {RequestTask} from 'src/app/shared/models/request-task.interface';
import {
  RELEASE_TYPE_LIST,
  ReleaseFormSubmitInterface,
  RISK_LEVEL_LIST,
  TypeAndRiskInterface
} from '../../../models/release.model';
import {RequestsService} from 'src/app/core/requests/requests.service';

@Component({
  selector: 'app-create-release-dialog',
  templateUrl: './create-release-dialog.component.html',
  styleUrls: ['./create-release-dialog.component.scss'],
  standalone: true,
  imports: [
    DpDatePickerModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatDialogModule,
    DialogCloseButtonComponent,
    InfoMessageComponent,
    TextFieldComponent,
    TextareaFieldComponent,
    DatetimePickerComponent,
    SelectModule,
    SecondaryButtonComponent,
    PrimaryButtonComponent,
    CommonModule
  ]
})
export class CreateReleaseDialogComponent implements OnInit {
  @Output() releaseCreated = new EventEmitter<Release>();
  @Output() switchToVersioning = new EventEmitter<void>();

  public releaseForm: UntypedFormGroup;
  public error: string | null = null;
  public isLoading = signal(false);

  public releaseTypeList: TypeAndRiskInterface[] = RELEASE_TYPE_LIST;
  public riskLevelList: TypeAndRiskInterface[] = RISK_LEVEL_LIST;

  constructor(private requestsSvc: RequestsService) {}

  ngOnInit(): void {
    this.releaseForm = new UntypedFormGroup({
      name: new UntypedFormControl('', this.nameValidator),
      description: new UntypedFormControl('', this.descriptionValidator),
      targetReleaseDate: new UntypedFormControl(
        this.getDate(),
        this.targetReleaseDateValidator.bind(this)
      ),
      type: new UntypedFormControl(1, this.riskValidator),
      riskLevel: new UntypedFormControl(1, this.riskValidator)
    });
  }

  get riskValidator() {
    return [Q9Validators.required(), Q9Validators.min(0), Q9Validators.max(2)];
  }

  get nameValidator() {
    return [Q9Validators.required(), Q9Validators.minLength(2), Q9Validators.maxLength(60)];
  }

  get descriptionValidator() {
    return [Q9Validators.minLength(0), Q9Validators.maxLength(5000)];
  }

  get taskForRelease(): RequestTask {
    const data: ReleaseFormSubmitInterface = {
      description: this.releaseForm.get('description').value,
      name: this.releaseForm.get('name').value,
      riskLevel: this.releaseForm.get('riskLevel').value,
      targetReleaseDate: this.getTargetReleaseDate(),
      type: this.releaseForm.get('type').value
    };

    return {
      isApi: true,
      method: 'POST',
      url: `/release`,
      data
    };
  }

  public async onCreateRelease() {
    if (this.releaseForm.invalid || this.isLoading()) {
      return;
    }

    try {
      this.isLoading.set(true);

      const release = await this.requestsSvc.addToQueue(this.taskForRelease, true);

      this.releaseCreated.emit(release);
    } catch (error) {
      const handledError = get(error, 'error', error);

      this.error = handledError?.error ?? handledError;
    } finally {
      this.isLoading.set(false);
    }
  }

  private getDate(): Date {
    const currentDate = new Date();

    currentDate.setUTCHours(0, 0, 0, 0);
    currentDate.setDate(currentDate.getDate() + 7);

    return utcToZonedTime(currentDate.toISOString(), 'UTC');
  }

  private targetReleaseDateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const formattedDate = format(new Date(control.value), 'yyyy-MM-dd HH:mm:ss');
    const newDate = getUnixTime(utcToZonedTime(formattedDate, 'UTC'));
    const today = getUnixTime(utcToZonedTime(new Date(), 'UTC'));

    return newDate < today ? {error: {message: 'You cannot set past date'}} : null;
  }

  private getTargetReleaseDate(): number {
    return +zonedTimeToUtc(this.releaseForm.get('targetReleaseDate').value, 'UTC');
  }
}
